import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CustomModal, Flex, Icon, UiText } from '@shared/ui';
import { getEstimateInformationQuery } from '@widgets/EstimateInformation/api/hooks/getEstimateInformationQuery';
import { useAddUsersModal } from '@widgets/EstimateInformation/lib/hooks/useAddUsersModal';
import { IEmployee } from '@widgets/EstimateInformation/model/types/types';
import { AddEmployeeInput } from '@widgets/EstimateInformation/ui/modals/AddUsersModal/AddEmployeeInput';
import { EmployeeCard } from '@widgets/EstimateInformation/ui/modals/AddUsersModal/EmployeesCard';

import cls from './AddEmployeesModal.module.css';

interface AddEmployeesModalProps {
    mode: 'managers' | 'purchasers' | null;
    onToggleModal: (value: 'managers' | 'purchasers' | null) => void;
}

export const AddEmployeesModal = ({ mode, onToggleModal }: AddEmployeesModalProps) => {
    useAddUsersModal();

    const { t } = useTranslation();

    const [addingUserEmail, setAddingUserEmail] = useState<string | null>(null);

    const onSetAddingUserEmail = (value: string | null): void => {
        setAddingUserEmail(value);
    };

    useEffect(() => {
        let timerId: number | null = null;

        if (addingUserEmail) {
            timerId = window.setTimeout(() => {
                setAddingUserEmail(null);
            }, 300);
        }

        return () => {
            if (timerId !== null) {
                clearTimeout(timerId);
            }
        };
    }, [addingUserEmail]);

    const { data } = getEstimateInformationQuery();

    const [inputMode, setInputMode] = useState<'add' | 'exist' | null>(null);
    const [employees, setEmployees] = useState<IEmployee[]>([]);

    const isEditMode =
        (mode === 'managers' && data?.managersData.canAdd) ||
        (mode === 'purchasers' && data?.purchasersData.canAdd);

    useEffect(() => {
        if (data) {
            if (mode === 'managers') setEmployees(data?.managersData?.managers);
            if (mode === 'purchasers') setEmployees(data?.purchasersData?.purchasers);
        }
    }, [data, mode]);

    const onChangeInputMode = (value: 'add' | 'exist' | null) => {
        setInputMode(value);
    };

    return (
        <CustomModal contentClasses={cls.modal} isOpen onHideModal={() => onToggleModal(null)}>
            <CustomModal.Header>
                {mode === 'managers'
                    ? t('AddEmployeesModal.return.CustomModal.Header.mode.managers')
                    : t('AddEmployeesModal.return.CustomModal.Header.mode.purchasers')}
            </CustomModal.Header>
            <CustomModal.Body>
                {!inputMode && isEditMode ? (
                    <Flex gap={4}>
                        <button
                            className={cls.addUserButton}
                            onClick={() => onChangeInputMode('add')}
                        >
                            <Icon iconName="group" />
                            <UiText variant="mediumPlain">
                                {t('AddEmployeesModal.return.CustomModal.Body.button.UiText.add')}
                            </UiText>
                        </button>
                        <button
                            className={cls.addUserButton}
                            onClick={() => onChangeInputMode('exist')}
                        >
                            <Icon iconName="plusLarge" />
                            <UiText variant="mediumPlain">
                                {t('AddEmployeesModal.return.CustomModal.Body.button.UiText.exist')}
                            </UiText>
                        </button>
                    </Flex>
                ) : (
                    <AddEmployeeInput
                        onSetAddingUserEmail={onSetAddingUserEmail}
                        onChangeInputMode={onChangeInputMode}
                        employees={data?.employees}
                        inputMode={inputMode}
                        modalMode={mode}
                    />
                )}

                <Flex gap={4} wrap="wrap" className={cls.EmployeesWrapper}>
                    {employees.map((employee) => (
                        <EmployeeCard
                            addingUserEmail={addingUserEmail}
                            employeesLength={employees.length}
                            mode={mode}
                            employee={employee}
                            key={employee.id}
                        />
                    ))}
                </Flex>
            </CustomModal.Body>
        </CustomModal>
    );
};
